import React, { useState, useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  ArrowRightIcon,
  ArrowDownTrayIcon,
  ComputerDesktopIcon,
  ShieldCheckIcon,
  FolderIcon,
  ClockIcon,
  DocumentIcon,
  UsersIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  KeyIcon,
  BellIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  getStepsForFlow,
  type OnboardingFlowType,
  type OnboardingStepConfig,
} from '../../config/onboardingConfig';

const STORAGE_KEY = 'governate_welcome_onboarding_progress';

interface WelcomeDownloadTourProps {
  onClose: () => void;
  onComplete: () => void;
}

const STEP_ICONS_USER = [
  ComputerDesktopIcon,
  FolderIcon,
  ComputerDesktopIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArrowDownTrayIcon,
];

const STEP_ICONS_ADMIN = [
  ArrowsRightLeftIcon, // Step 0: User & Admin mode switch
  DocumentIcon,
  FolderIcon,
  UsersIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  ShieldCheckIcon,
  KeyIcon,
  BellIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
];

const STEP_ICONS_READER = [DocumentIcon, BellIcon, ArrowDownTrayIcon];

/** Last-step fallbacks per flow (key -> default value) when translation missing */
const LAST_STEP_FALLBACKS: Record<OnboardingFlowType, { titleKey: string; title: string; descriptionKey: string; description: string }> = {
  user: {
    titleKey: 'step6.title',
    title: 'Download',
    descriptionKey: 'step6.description',
    description: 'Get the Governate client to protect your files with embedded security, policy, and control.',
  },
  admin: {
    titleKey: 'adminStep12.title',
    title: 'Organisations',
    descriptionKey: 'adminStep12.description',
    description: 'Manage child organisations and company structure.',
  },
  reader: {
    titleKey: 'readerStep3.title',
    title: 'Download',
    descriptionKey: 'readerStep3.description',
    description: 'Get the Governate client to open and work with protected files.',
  },
};

function buildStepsDetail(
  indices: number[],
  getText: (key: string, fallback?: string) => string,
  stepsConfig: readonly OnboardingStepConfig[],
  flowType: OnboardingFlowType
): { index: number; title: string; description: string }[] {
  const lastFallbacks = LAST_STEP_FALLBACKS[flowType];
  const lastIndex = stepsConfig.length - 1;
  return indices.map((idx) => {
    const row = stepsConfig[idx];
    const isLast = idx === lastIndex;
    const title = row
      ? (isLast ? getText(row.titleKey, lastFallbacks.title) : getText(row.titleKey))
      : '';
    const description = row
      ? (isLast ? getText(row.descriptionKey, lastFallbacks.description) : getText(row.descriptionKey))
      : '';
    return { index: idx, title, description };
  });
}

/** Determine if the user gets the admin onboarding flow (org-wide steps). */
function isAdminFlow(user: { role?: string } | null): boolean {
  if (!user?.role || typeof user.role !== 'string') return false;
  const r = user.role.trim().toLowerCase();
  return r === 'admin' || r === 'sysadmin';
}

const WelcomeDownloadTour: React.FC<WelcomeDownloadTourProps> = ({ onClose, onComplete }) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('onboarding');
  const { setCurrentStep, setFlowType } = useOnboarding();

  // Derive flow from current user: reader mode gets 3-step flow; admin role gets admin flow; else user flow
  const flowType: OnboardingFlowType =
    user?.mode === 'reader' ? 'reader' : isAdminFlow(user) ? 'admin' : 'user';
  const stepsConfig = getStepsForFlow(flowType);
  const stepCount = stepsConfig.length;
  const stepIcons =
    flowType === 'admin' ? STEP_ICONS_ADMIN : flowType === 'reader' ? STEP_ICONS_READER : STEP_ICONS_USER;

  const [step, setStep] = useState(0);
  const [stepsViewed, setStepsViewed] = useState<number[]>([0]);

  useLayoutEffect(() => {
    setFlowType(flowType);
  }, [flowType, setFlowType]);

  // When flow changes (e.g. user loaded with admin role), reset to first step
  useLayoutEffect(() => {
    setStep(0);
    setStepsViewed([0]);
  }, [flowType]);

  useLayoutEffect(() => {
    setCurrentStep(step);
    return () => setCurrentStep(null);
  }, [step, setCurrentStep]);

  useLayoutEffect(() => {
    setStepsViewed((prev) => (prev.includes(step) ? prev : [...prev, step].sort((a, b) => a - b)));
  }, [step]);

  const getText = useCallback(
    (key: string, fallback?: string) =>
      fallback !== undefined ? t(key, { defaultValue: fallback }) : t(key),
    [t]
  );

  const saveProgress = useCallback(
    async (lastStepIndex: number, completed: boolean, detailsPayload?: Record<string, unknown>) => {
      const stepsViewedForDetails = (detailsPayload?.steps_viewed as number[] | undefined) ?? stepsViewed;
      const stepsDetail =
        (detailsPayload?.steps_detail as { index: number; title: string; description: string }[] | undefined) ??
        buildStepsDetail(stepsViewedForDetails, getText, stepsConfig, flowType);
      const details = detailsPayload ?? {
        flow_type: flowType,
        steps_viewed: stepsViewed,
        closed_at_step: lastStepIndex,
        skipped: false,
        completed_at: completed ? new Date().toISOString() : undefined,
      };
      const detailsWithSteps = { ...details, steps_detail: stepsDetail };
      try {
        await apiService.patch('/account/onboarding', {
          lang: i18n.language || 'en',
          last_step_index: lastStepIndex,
          completed,
          details: detailsWithSteps,
        });
      } catch (_) {
        // non-blocking
      }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ lastStepIndex, completed, flowType, details: detailsWithSteps }));
      } catch (_) {}
    },
    [i18n.language, stepsViewed, getText, stepsConfig, flowType]
  );

  const completeAndClose = useCallback(
    async (didSkip?: boolean) => {
      const wasSkipped = didSkip ?? false;
      const stepsDetail = buildStepsDetail(stepsViewed, getText, stepsConfig, flowType);
      await saveProgress(step, true, {
        flow_type: flowType,
        steps_viewed: stepsViewed,
        steps_detail: stepsDetail,
        closed_at_step: step,
        skipped: wasSkipped,
        completed_at: new Date().toISOString(),
      });
      setCurrentStep(null);
      onComplete();
      onClose();
    },
    [step, stepsViewed, getText, stepsConfig, flowType, saveProgress, setCurrentStep, onComplete, onClose]
  );

  const handleClose = useCallback(() => completeAndClose(false), [completeAndClose]);
  const handleSkip = useCallback(() => completeAndClose(true), [completeAndClose]);

  const handleNext = useCallback(async () => {
    if (step < stepCount - 1) {
      const nextStep = step + 1;
      setStep(nextStep);
      const stepsViewedIncludingNext = Array.from(new Set([...stepsViewed, nextStep])).sort((a, b) => a - b);
      const stepsDetail = buildStepsDetail(stepsViewedIncludingNext, getText, stepsConfig, flowType);
      await saveProgress(nextStep, false, {
        flow_type: flowType,
        steps_viewed: stepsViewedIncludingNext,
        steps_detail: stepsDetail,
        closed_at_step: nextStep,
        skipped: false,
      });
    } else {
      await completeAndClose();
    }
  }, [step, stepCount, stepsViewed, getText, stepsConfig, flowType, saveProgress, completeAndClose]);

  const isLastStep = step === stepCount - 1;
  const currentStepConfig = stepsConfig[step];
  const lastFallbacks = LAST_STEP_FALLBACKS[flowType];
  const title = currentStepConfig
    ? getText(currentStepConfig.titleKey, isLastStep ? lastFallbacks.title : undefined)
    : '';
  const description = currentStepConfig
    ? getText(currentStepConfig.descriptionKey, isLastStep ? lastFallbacks.description : undefined)
    : '';
  const Icon = stepIcons[step] ?? ShieldCheckIcon;

  return (
    <>
      {/* Backdrop at z-[100] so sidebar (z-[101]) and modal (z-[102]) can sit above it */}
      <div className="fixed inset-0 z-[100]" aria-hidden>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      </div>
      <div className="fixed inset-0 z-[102] flex items-center justify-center p-4 pointer-events-none">
        <div className="relative bg-gray-900 dark:bg-gray-800 border border-gray-600 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden pointer-events-auto">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
        <div className="p-6 pt-10 pb-8">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <Icon className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-white text-center mb-2">{title}</h2>
          <p className="text-sm text-gray-400 text-center mb-6">{description}</p>
          <div className="flex justify-center gap-2 mb-4">
            {stepsConfig.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-green-500' : 'w-1.5 bg-gray-600'
                }`}
              />
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {!isLastStep && (
              <button
                type="button"
                onClick={handleSkip}
                className="px-4 py-2.5 text-sm text-gray-400 hover:text-white transition-colors"
              >
                {t('skipAll')}
              </button>
            )}
            <div className="flex-1 flex justify-end gap-2">
              {isLastStep ? (
                <button
                  type="button"
                  onClick={handleClose}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-500 text-white font-medium text-sm transition-colors"
                >
                  {t('getStarted')}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-500 text-white font-medium text-sm transition-colors"
                >
                  {t('next')}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default WelcomeDownloadTour;
export { STORAGE_KEY };
